import { useCallback, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { Outlet } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import {
  getDefaultUnderwritingQuestionsForm,
  mapUnderwritingQuestionsFormToQueryInput,
  toUnderwritingQuestionsObject,
  UnderwritingQuestionsForm,
  underwritingQuestionsFormValidation,
  UnderwritingQuestionsModal,
} from "@covertree/new-business-forms";
import {
  selectIsTenantFlow,
  selectPolicy,
  selectPolicyStateFromAddress,
  selectUnderwritingAnswers,
  selectUnderwritingQuestions,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Form, LegalTypography } from "@covertree/ui";
import { useFormWithNotification } from "@covertree/hooks";
import { HomeConditions } from "./UwHomeConditions";
import { HomeOptions } from "./UwHomeOptions";
import { useUwModal } from "./useUwModal";
import {
  HandleSubmition,
  OnSubmitionError,
  OnSubmitionStarted,
  OnSubmitionSuccess,
  UwContext,
} from "./types";

interface UwFormProviderProps {
  isD2C: boolean;
  handleSubmition: HandleSubmition;
  onSubmitionError: OnSubmitionError;
  onSubmitionStarted: OnSubmitionStarted;
  onSubmitionSuccess: OnSubmitionSuccess;
}

function UwQuestionsForm({
  isD2C,
  handleSubmition,
  onSubmitionError,
  onSubmitionStarted,
  onSubmitionSuccess,
}: UwFormProviderProps) {
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const { modalConditionData, hasAnswerChanged, setModalCondition } =
    useUwModal();

  const { units } = useNewBusinessSelector(selectPolicy)!;
  const isTenantFlow = useNewBusinessSelector(selectIsTenantFlow);
  const underwritingAnswers = useNewBusinessSelector(selectUnderwritingAnswers);
  const state = useNewBusinessSelector(selectPolicyStateFromAddress);
  const questions = useNewBusinessSelector(selectUnderwritingQuestions);

  const questionsObject = useMemo(
    () => toUnderwritingQuestionsObject(questions),
    [questions]
  );

  const form = useFormWithNotification<UnderwritingQuestionsForm>({
    mode: "all",
    delayError: FORM_ERROR_DELAY_IN_MS,
    defaultValues: getDefaultUnderwritingQuestionsForm(underwritingAnswers),
    resolver: yupResolver(underwritingQuestionsFormValidation(questionsObject)),
  });

  const submitUnderwritingQuestions = useCallback(
    async (values: UnderwritingQuestionsForm) => {
      setIsFormSubmitting(true);

      onSubmitionStarted();

      try {
        const { policyLevelUW, unitLevelUW } =
          mapUnderwritingQuestionsFormToQueryInput(
            units[0].exposureLocator,
            values,
            isTenantFlow,
            questionsObject
          );

        await handleSubmition({ policyLevelUW, unitLevelUW });

        onSubmitionSuccess();
      } catch {
        onSubmitionError();
      }

      setIsFormSubmitting(false);
    },
    []
  );

  const uwContext: UwContext = {
    state,
    questions: questionsObject,
    isFormSubmitting,
    setModalCondition,
  };

  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(submitUnderwritingQuestions)}>
        {isD2C ? (
          <>
            <Outlet context={uwContext} />
            <LegalTypography variant="subtitle1" mt={5}>
              You will be asked to verify your responses with your signature
              prior to your purchase.
            </LegalTypography>
          </>
        ) : (
          <>
            <HomeOptions isD2C={false} context={uwContext} />
            <HomeConditions isD2C={false} context={uwContext} />
          </>
        )}
        <UnderwritingQuestionsModal
          condition={modalConditionData}
          hasAnswerChanged={hasAnswerChanged}
        />
      </Form>
    </FormProvider>
  );
}

export { UwQuestionsForm };
